/**
 * Catalog of internal SPA URL patterns and the builders that produce a
 * concrete URL from each. routes.js consumes a builder's `.pattern` to
 * declare its route; navigation callsites (links, `navigate()`,
 * redirects) call the builder itself. Not every exported builder is
 * consumed by routes.js yet — pages still mounted as relative child
 * routes keep their literal path segments in place.
 *
 * - definePath — compiles a pattern into a param-checked builder
 * - one exported builder per routed page, grouped by area
 *
 * Returns plain JS — no JSX.
 */

/**
 * Compiles a react-router path pattern into a builder that renders a
 * concrete URL from params, failing fast on any missing param.
 *
 * @param {string} pattern - a react-router path pattern, e.g. `/c/:cust_id/plants`.
 * @returns {((params?: Object.<string, string|number>) => string) & { pattern: string }}
 *   A builder function carrying its source `pattern` as a property.
 */
const definePath = pattern => {
  const segments = pattern.split(/:(\w+)/);
  const build = (params = {}) =>
    segments
      .map((segment, index) => {
        if (index % 2 === 0) return segment;
        const value = params[segment];
        if (value === undefined || value === null) {
          throw new Error(`Missing "${segment}" for path pattern ${pattern}`);
        }
        return value;
      })
      .join('');
  build.pattern = pattern;
  return build;
};

// Auth
export const rootPath = definePath('/');
export const loginPath = definePath('/login');
export const logoutPath = definePath('/logout');
export const authCallbackPath = definePath('/callback');

// Top-level utility pages
export const bluetoothDebugPath = definePath('/btdebug');
export const searchPath = definePath('/search');

// Support pages — customers, users, radios
export const supportCustomersPath = definePath('/support/customers');
export const supportCustomerCreatePath = definePath('/support/customers/new');
export const supportRadioDetailPath = definePath('/support/radios/:radio_id');
export const supportUsersPath = definePath('/support/users');
export const supportUserCreatePath = definePath('/support/users/new');
export const supportUserPath = definePath('/support/users/:user_id');
export const supportUserSettingsPath = definePath('/support/users/:user_id/settings');
export const supportUserSettingsSectionPath = definePath('/support/users/:user_id/settings/:section');
export const supportUserManualPasswordPath = definePath('/support/users/:user_id/manualpassword');

// Personal user settings
export const userHomePath = definePath('/user');
export const userHomeSectionPath = definePath('/user/:section');
export const userSettingsPath = definePath('/user/settings');
export const userSettingsSectionPath = definePath('/user/settings/:section');

// Customer pages
export const customerHomeRedirectPath = definePath('/c/:cust_id/home');
export const customerPath = definePath('/c/:cust_id');
export const customerPlantsPath = definePath(`${customerPath.pattern}/plants`);
export const customerUsersPath = definePath(`${customerPath.pattern}/users`);
export const customerReportsPath = definePath(`${customerPath.pattern}/reports`);
export const customerAlarmLiveSummaryPath = definePath(`${customerPath.pattern}/reports/alarmlivesummary`);
export const customerCurfewReportPath = definePath(`${customerPath.pattern}/reports/curfew`);
export const customerOperationsDashboardPath = definePath(`${customerPath.pattern}/dashboards/operations`);
export const customerManagementDashboardPath = definePath(`${customerPath.pattern}/dashboards/management`);
export const customerMapPath = definePath(`${customerPath.pattern}/map`);
export const customerMapSitePath = definePath(`${customerPath.pattern}/map/site/:site_id`);
export const customerUserCreatePath = definePath(`${customerPath.pattern}/users/new`);
export const customerUserPath = definePath(`${customerPath.pattern}/users/:user_id`);
export const customerUserSettingsPath = definePath(`${customerPath.pattern}/users/:user_id/settings`);
export const customerUserSettingsSectionPath = definePath(`${customerPath.pattern}/users/:user_id/settings/:section`);
export const customerUserManualPasswordPath = definePath(`${customerPath.pattern}/users/:user_id/manualpassword`);
export const customerSettingsPath = definePath(`${customerPath.pattern}/settings`);
export const customerSettingsSectionPath = definePath(`${customerPath.pattern}/settings/:section`);
export const customerContactCreatePath = definePath('/c/:customerId/contacts/new');
export const customerContactPath = definePath('/c/:customerId/contacts/:contactId');

// Site group pages
export const siteGroupPath = definePath(`${customerPath.pattern}/plant/:sitegroup_id`);
export const siteGroupStructuresPath = definePath(`${siteGroupPath.pattern}/structures`);
export const siteGroupMapPath = definePath(`${siteGroupPath.pattern}/map`);
export const siteGroupMapSitePath = definePath(`${siteGroupPath.pattern}/map/site/:site_id`);
export const siteGroupCompliancePath = definePath(`${siteGroupPath.pattern}/compliance`);
export const siteGroupUsersPath = definePath(`${siteGroupPath.pattern}/users`);
export const siteGroupSupportPath = definePath(`${siteGroupPath.pattern}/support`);
export const siteGroupSupportTicketsPath = definePath(`${siteGroupPath.pattern}/support/tickets`);
export const siteGroupUserCreatePath = definePath(`${siteGroupPath.pattern}/users/new`);
export const siteGroupUserPath = definePath(`${siteGroupPath.pattern}/users/:user_id`);
export const siteGroupUserSettingsPath = definePath(`${siteGroupPath.pattern}/users/:user_id/settings`);
export const siteGroupUserSettingsSectionPath = definePath(`${siteGroupPath.pattern}/users/:user_id/settings/:section`);
export const siteGroupUserManualPasswordPath = definePath(`${siteGroupPath.pattern}/users/:user_id/manualpassword`);
export const siteGroupSettingsPath = definePath(`${siteGroupPath.pattern}/settings`);
export const siteGroupSettingsSectionPath = definePath(`${siteGroupPath.pattern}/settings/:section`);
export const siteGroupContactCreatePath = definePath('/c/:customerId/plant/:siteGroupId/contacts/new');
export const siteGroupContactPath = definePath('/c/:customerId/plant/:siteGroupId/contacts/:contactId');
export const siteGroupInventoryPath = definePath('/c/:customerId/plant/:siteGroupId/inventory');

/**
 * Where opening a site group lands.
 *
 * An inventory group holds stock and can never hold a structure, so its
 * structures grid is permanently empty; inventory is the only view that has
 * anything to show.
 *
 * @param {{id: number|string, is_inventory?: boolean}} siteGroup
 * @param {number|string} customerId
 * @returns {string}
 */
export const siteGroupLandingPath = (siteGroup, customerId) =>
  siteGroup?.is_inventory
    ? siteGroupInventoryPath({ customerId, siteGroupId: siteGroup.id })
    : siteGroupStructuresPath({ cust_id: customerId, sitegroup_id: siteGroup.id });

// Customer reports
export const customerInventoryPath = definePath('/c/:customerId/inventory');
export const customerMaintenanceReportPath = definePath(`${customerPath.pattern}/reports/maintenance`);
export const customerOfflineReportRedirectPath = definePath(`${customerPath.pattern}/reports/offline`);

// Site group reports
export const siteGroupReportsPath = definePath(`${siteGroupPath.pattern}/reports`);
export const siteGroupDailySolarPath = definePath(`${siteGroupReportsPath.pattern}/dailysolar`);
export const siteGroupWeeklySolarPath = definePath(`${siteGroupReportsPath.pattern}/weeklysolar`);
export const siteGroupMonthlySolarPath = definePath(`${siteGroupReportsPath.pattern}/monthlysolar`);
export const siteGroupYearlySolarPath = definePath(`${siteGroupReportsPath.pattern}/yearlysolar`);
export const siteGroupLoadSchedulesPath = definePath(`${siteGroupReportsPath.pattern}/loadschedules`);
export const siteGroupReportsAlarmLiveSummaryPath = definePath(`${siteGroupReportsPath.pattern}/alarmlivesummary`);
export const siteGroupDisabledLoadsPath = definePath(`${siteGroupReportsPath.pattern}/disabledloads`);
export const siteGroupDigitalMaintenancePath = definePath(`${siteGroupReportsPath.pattern}/digitalmaintenance`);
export const siteGroupFixtureReportPath = definePath(`${siteGroupReportsPath.pattern}/fixture`);
export const siteGroupProofOfPerformancePath = definePath(`${siteGroupPath.pattern}/pop`);
export const siteGroupMaintenanceReportPath = definePath(`${siteGroupPath.pattern}/reports/maintenance`);

// RMA (returns)
export const siteGroupRmaListPath = definePath(`${siteGroupPath.pattern}/support/rmas`);
export const siteGroupRmaViewPath = definePath(`${siteGroupPath.pattern}/support/rma/:rma_id/view`);
export const rmaListPath = definePath('/rmas');
export const rmaBatchPrintPath = definePath('/rmas/print');
export const rmaViewPath = definePath('/rma/:rma_id/view');
export const rmaCreatePath = definePath('/rma/new');
export const rmaIssuePath = definePath('/rma/:rma_id/issue');
export const rmaQuickShipPath = definePath('/rma/:rma_id/quickship');
export const rmaProcessPath = definePath('/rma/:rma_id/process');
export const rmaInvoicePath = definePath('/rma/:rma_id/invoice');
export const rmaResultSummaryPath = definePath('/rma/:rma_id/resultsummary');

// Tickets
export const siteGroupTicketCreatePath = definePath(`${siteGroupPath.pattern}/ticket/new`);

// Site pages
export const sitePath = definePath(`${siteGroupPath.pattern}/structure/:site_id`);
export const siteConfigurationPath = definePath(`${sitePath.pattern}/configuration`);
export const siteMediaPath = definePath(`${sitePath.pattern}/media`);
export const siteDeviceImagesPath = definePath(`${sitePath.pattern}/deviceimages`);
export const sitePopPath = definePath(`${sitePath.pattern}/pop`);
export const siteStatusHistoryPath = definePath(`${sitePath.pattern}/statushist`);
export const siteCalendarPath = definePath(`${sitePath.pattern}/calendar`);
export const siteSettingsPath = definePath(`${sitePath.pattern}/settings`);
export const siteSettingsSectionPath = definePath(`${sitePath.pattern}/settings/:section`);
export const siteMaintenanceHistoryPath = definePath(`${sitePath.pattern}/maintenancehist`);
export const siteAlarmHistoryPath = definePath(`${sitePath.pattern}/alarmhist`);
export const siteCompliancePath = definePath(`${sitePath.pattern}/compliance`);
export const siteActivityLogPath = definePath(`${sitePath.pattern}/activity-log`);
export const siteCommandLogPath = definePath(`${sitePath.pattern}/command-log`);
export const siteStatusesPath = definePath(`${sitePath.pattern}/statuses`);
export const siteLegacyParamsPath = definePath(`${sitePath.pattern}/params`);
export const siteThresholdsPath = definePath(`${sitePath.pattern}/thresholds`);
export const siteScheduleCommandsPath = definePath(`${sitePath.pattern}/schedulecommands`);
export const siteCicStatusHistoryPath = definePath(`${sitePath.pattern}/cicstatushist`);
export const siteChartingPath = definePath(`${sitePath.pattern}/charting`);
export const siteReadingsPath = definePath(`${sitePath.pattern}/readings`);
export const siteReadingsPowerPath = definePath(`${sitePath.pattern}/readings/power`);
export const siteMeasurementsPath = definePath(`${sitePath.pattern}/measurements`);
export const siteReadingsPowerMeterPath = definePath(`${sitePath.pattern}/readings/power/meter`);
export const siteReadingsPowerConsumptionPath = definePath(`${sitePath.pattern}/readings/power/consumption`);
export const siteReadingsPowerSolarPath = definePath(`${sitePath.pattern}/readings/power/solar`);
export const siteReadingsPowerAnsiPath = definePath(`${sitePath.pattern}/readings/power/ansi`);
export const siteSolarOptionsPath = definePath(`${sitePath.pattern}/solaroptions`);
export const siteTicketCreatePath = definePath(`${sitePath.pattern}/ticket/new`);
export const siteTicketsPath = definePath(`${sitePath.pattern}/tickets`);

// Support pages (tickets)
export const supportTicketEditPath = definePath('/support/ticket/:ticket_id/edit');
export const supportTicketViewPath = definePath('/support/ticket/:ticket_id/view');

// Error page
export const genericErrorPath = definePath('/error/:error_id');

// Billing
export const billingDashboardPath = definePath('/billing');
export const billingInvoicePath = definePath('/billing/invoices/:invoiceId');
export const billingAccountsPath = definePath('/billing/accounts');
export const billingAccountPath = definePath('/billing/accounts/:accountId');
export const billingAccountPricingPath = definePath('/billing/accounts/:accountId/pricing');
export const billingAccountInvoicesPath = definePath('/billing/accounts/:accountId/invoices');
export const billingAccountTrialPath = definePath('/billing/accounts/:accountId/trial');
export const billingAccountCatalogPath = definePath('/billing/accounts/:accountId/catalog');
export const billingAccountPrepaidPath = definePath('/billing/accounts/:accountId/prepaid');
export const billingPrepaidTrackingPath = definePath('/billing/prepaid-tracking');
export const billingTrialUnitsPath = definePath('/billing/trial-units');

// Admin / staff
export const staffHomePath = definePath('/staff');
export const staffFirmwareMatrixPath = definePath('/staff/firmwarematrix');
export const staffFirmwareUpgradesPath = definePath('/staff/firmware-upgrades');
export const staffFirmwareUpgradeCreatePath = definePath('/staff/firmware-upgrades/new');
export const staffFirmwareUpgradeBatchPath = definePath('/staff/firmware-upgrades/:batchId');
export const staffQueueStatusPath = definePath('/staff/queuestatus');
export const staffWarrantyStatusPath = definePath('/staff/warrantystatus/');
export const staffBillingQuickbooksPath = definePath('/staff/billing/quickbooks');
export const staffWarrantyFeePath = definePath('/staff/warrantyfee/');
export const staffRmaFeePath = definePath('/staff/rmafee/');
export const staffStationPath = definePath('/staff/station/');
export const staffIssueCategoriesPath = definePath('/staff/issuecategories/');
export const staffSystemNoticesPath = definePath('/staff/systemnotices/');
export const staffSystemNoticePath = definePath('/staff/systemnotices/:id/');
export const staffTamperIssuesPath = definePath('/staff/tamperissues');
export const staffActivationQueryPath = definePath('/staff/activationquery/');
export const staffExcessiveAlarmsPath = definePath('/staff/excessive-alarms/');
export const staffEthernetStatsPath = definePath('/staff/ethernet/');

// Sales (orders)
export const salesHomePath = definePath('/sales');
export const salesOrdersPath = definePath('/sales/orders');
export const salesPeoplePath = definePath('/sales/people');
export const salesProductsPath = definePath('/sales/products');
export const salesInventoryLogPath = definePath('/sales/reports/inventory-log');
export const salesProductCategoriesPath = definePath('/sales/products/categories');
export const salesProductCreatePath = definePath('/sales/products/new');
export const salesProductPath = definePath('/sales/products/:productId');
export const salesOrderCreatePath = definePath('/sales/orders/new');
export const salesOrderPrintPath = definePath('/sales/orders/:orderId/print');
export const salesOrderPath = definePath('/sales/orders/:orderId');

// Services (ODL services)
export const servicesHomePath = definePath('/services/');
export const servicesDefinitionsPath = definePath('/services/definitions/');
export const servicesProjectsRedirectPath = definePath('/services/projects');
export const servicesCustomerPricesPath = definePath('/services/customer/:customerId/prices/');
export const servicesProjectPath = definePath('/services/customer/:customerId/project/:projectId');
export const servicesProjectSiteGroupPath = definePath(
  '/services/customer/:customerId/project/:projectId/sitegroup/:siteGroupId',
);
export const servicesSiteJobsPath = definePath(
  '/services/customer/:customerId/project/:projectId/sitegroup/:siteGroupId/site/:siteId/jobs',
);
export const servicesWorkFormPath = definePath(
  '/services/customer/:customerId/project/:projectId/sitegroup/:siteGroupId/site/:siteId',
);
export const servicesWorkFormJobPath = definePath(
  '/services/customer/:customerId/project/:projectId/sitegroup/:siteGroupId/site/:siteId/job/:jobId',
);
export const servicesReportingPath = definePath('/services/reporting');
export const servicesBillingRedirectPath = definePath('/services/customer/:customerId/project/:projectId/billing');
export const servicesCrewsPath = definePath('/services/crews');

// Entity reference resolver routes — the link target for a cross-entity
// reference chip. Each mounts EntityRedirect, which looks up the entity's
// real page and navigates there.
export const customerResolverPath = definePath('/customers/:customer_id');
export const siteGroupResolverPath = definePath('/site-groups/:sitegroup_id');
export const siteResolverPath = definePath('/sites/:site_id');
export const userResolverPath = definePath('/users/:user_id');
export const radioResolverPath = definePath('/radios/:radio_id');
